import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { ChangeEvent, useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppSelector, useToast } from '../../hooks';
import { ReportInfo } from '../../pages/ListReports';
import { apiService } from '../../services/api';
import {
  ClassEvent,
  ReportEntity,
  ReportStatus,
  StudentPerformance,
  UpdateReportReq,
  UserRole,
} from '../../services/api.types';
import { formatMoney, formatStringMoney } from '../../utils/formatters';
import { Button } from '../Button';
import { Datepicker } from '../Datepicker';
import { DraggableModal, DraggableModalProps } from '../DraggableModal';
import { Input } from '../Inputs/Input';
import { TextArea } from '../Inputs/TextArea';
import { Radio } from '../Radio';
import { formRules } from '../RegisterForm/FormRules';
import { Select } from '../Select';
import { Switch } from '../Switch';
import { Timepicker } from '../Timepicker';
import { ModalReviewReq } from './ModalReviewReq';

export interface ReportFormValues {
  fullName: string;
  email: string;
  date: Dayjs | null;
  startTimeClass: Dayjs | null;
  endTimeClass: Dayjs | null;
  studentName: string;
  responsibleFullName: string;
  classLocation: string;
  taughtContent: string;
  studentPerformance: StudentPerformance;
  generalObservation: string;
  status: ReportStatus;
  valueFromResponsible: string;
  valueToTeacher: string;
  valueToTrip: string;
  wasStudentAbsent: boolean;
  reviewRequest: string;
}

const studentPerformance = [
  { value: StudentPerformance.IMPROVED, label: 'Sim, melhorou' },
  { value: StudentPerformance.WORSE, label: 'Não, piorou' },
  { value: StudentPerformance.SAME, label: 'Não, manteve-se igual' },
  {
    value: StudentPerformance.FIRST,
    label: 'Não há como comparar, foi a primeira aula',
  },
];

const options = [
  { value: ReportStatus.PENDING, label: 'Pendente' },
  { value: ReportStatus.DONE, label: 'Feito' },
];

interface TeacherReportProps extends Omit<DraggableModalProps, 'children'> {
  onChangeEvent?: (res: ClassEvent) => Promise<void> | void | undefined;
  onClose: () => void;
  onUpdate?: (res: ReportEntity) => void;
  reportInfo?: ReportInfo;
  hideButtons?: boolean;
}

export function TeacherReport({
  onClose,
  onUpdate,
  onChangeEvent,
  reportInfo,
  hideButtons = false,
  ...draggableModalProps
}: TeacherReportProps) {
  const reportDefaultValues: ReportFormValues = {
    email: reportInfo?.email ?? '',
    fullName: reportInfo?.fullName ?? '',
    date: reportInfo?.date ? dayjs(reportInfo.date) : null,
    startTimeClass: reportInfo?.startTimeClass
      ? dayjs(reportInfo.startTimeClass)
      : null,
    endTimeClass: reportInfo?.endTimeClass
      ? dayjs(reportInfo.endTimeClass)
      : null,
    studentName: reportInfo?.studentName ?? '',
    responsibleFullName: reportInfo?.responsibleFullName ?? '',
    classLocation: reportInfo?.classLocation ?? '',
    taughtContent: reportInfo?.taughtContent ?? '',
    studentPerformance:
      reportInfo?.studentPerformance ?? StudentPerformance.FIRST,
    generalObservation: reportInfo?.generalObservation ?? '',
    status: reportInfo?.status ?? ReportStatus.PENDING,
    valueFromResponsible: formatMoney(reportInfo?.valueFromResponsible ?? 0),
    valueToTeacher: formatMoney(reportInfo?.valueToTeacher ?? 0),
    valueToTrip: formatMoney(reportInfo?.valueToTrip ?? 0),
    wasStudentAbsent: reportInfo?.wasStudentAbsent ?? false,
    reviewRequest: reportInfo?.reviewRequest ?? '',
  };
  const methods = useForm<ReportFormValues>({
    mode: 'all',
    defaultValues: reportDefaultValues,
  });
  const toast = useToast();
  const status = methods.watch('status');
  const studentAbsent = methods.watch('wasStudentAbsent');
  const reviewReqFormField = methods.watch('reviewRequest');

  const user = useAppSelector((state) => state.user);
  const isAdmOrEmployee =
    user?.role === UserRole.ADMIN || user?.role === UserRole.EMPLOYEE;
  const isTeacher = user?.role === UserRole.TEACHER;
  const [isUpdatingReport, setIsUpdatingReport] = useState<boolean>(false);
  const canTeacherUpdates =
    reportInfo?.status === ReportStatus.PENDING && isTeacher;

  const enableTeachertAlert: boolean =
    status === ReportStatus.PENDING && reviewReqFormField !== '' && isTeacher;

  const [openReviewModal, setOpenReviewModal] =
    useState<boolean>(enableTeachertAlert);

  const handleStatusChange = (value: ReportStatus) => {
    value && methods.setValue('status', value);
    if (value === ReportStatus.DONE) {
      methods.setValue('reviewRequest', '');
      setOpenReviewModal(false);
    } else {
      methods.setValue('reviewRequest', reportDefaultValues.reviewRequest);
      setOpenReviewModal(true);
    }
  };

  useEffect(() => {
    if (studentAbsent) {
      methods.setValue('studentPerformance', StudentPerformance.SAME);
      methods.setValue('taughtContent', ' ');
    }
  }, [methods, studentAbsent]);

  const onSubmit = async (data: ReportFormValues) => {
    let request: UpdateReportReq = {};
    if (canTeacherUpdates) {
      request = {
        studentPerformance: data.studentPerformance,
        taughtContent: data.taughtContent,
        wasStudentAbsent: data.wasStudentAbsent,
        observations: data.generalObservation,
      };
    } else if (isAdmOrEmployee) {
      request = {
        status: data.status,
        reviewRequest: data.reviewRequest,
      };
    } else {
      toast.error('Você não tem permissão para alterar este relatório');
      return;
    }

    setIsUpdatingReport(true);
    await apiService
      .updateReport(Number(reportInfo?.id), request)
      .then(async (res) => {
        onUpdate?.(res);

        if (onChangeEvent) {
          const event = await apiService.getEvent(res.classId);
          await onChangeEvent(event);
        }

        methods.reset(reportDefaultValues);
        toast.success('Relatório alterado!');
        onClose();
      })
      .finally(() => {
        setIsUpdatingReport(false);
      });
  };

  const handleChangeMoneyMask = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    field: keyof ReportFormValues
  ) => {
    methods.setValue(field, formatStringMoney(event.target.value));
  };

  return (
    <DraggableModal
      {...draggableModalProps}
      onClose={onClose}
      onReportAlert={
        (enableTeachertAlert || isAdmOrEmployee) && !hideButtons
          ? () => {
              setOpenReviewModal(true);
              methods.setValue(
                'reviewRequest',
                reportDefaultValues.reviewRequest
              );
              methods.setValue('status', ReportStatus.PENDING);
              draggableModalProps.onReportAlert?.();
            }
          : undefined
      }
    >
      <div className="h-full flex flex-col">
        <div className="bg-white rounded-t-[45px] px-8 py-3 relative">
          {isAdmOrEmployee && (
            <div className="w-full max-w-[250px] float-right">
              <Select
                label={'Alterar Status'}
                id={'filter-table'}
                value={status}
                onChange={(value) => handleStatusChange(value)}
                size={'small'}
                options={options}
                className="w-full text-start"
                disabled={hideButtons}
              />
            </div>
          )}
        </div>
        <div className="w-full scroll-table bg-white p-3 max-h-full overflow-auto">
          <FormProvider {...methods}>
            <form
              className="flex flex-col items-center gap-2 w-full box-border pt-2 p-4 justify-between"
              onSubmit={methods.handleSubmit(onSubmit)}
            >
              <Input
                label="Email"
                name="email"
                type="email"
                disabled
                rules={formRules.emailRequired}
              />
              <Input
                label="Nome do Professor"
                name="fullName"
                disabled
                rules={formRules.fullNameRequired}
              />
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="pt-br"
              >
                <Datepicker
                  className="w-full"
                  labelOut
                  label="Data da Aula"
                  name="date"
                  disabled
                  rules={formRules.dateRequired}
                />
                <Timepicker
                  className="w-full"
                  labelOut
                  label="Hora de Inicio da Aula"
                  name="startTimeClass"
                  disabled
                  rules={formRules.timeRequired}
                />
                <Timepicker
                  className="w-full"
                  labelOut
                  label="Hora de Término da Aula"
                  name="endTimeClass"
                  disabled
                  rules={formRules.timeRequired}
                />
              </LocalizationProvider>
              <Input
                label="Nome do Aluno"
                name="studentName"
                disabled
                rules={formRules.fullNameRequired}
              />
              <Input
                label="Nome do Responsável"
                name="responsibleFullName"
                disabled
              />
              <Input
                label="Local da Aula"
                name="classLocation"
                disabled
                rules={formRules.classLocationRequired}
              />
              {reportInfo?.valueFromResponsible !== undefined && (
                <Input
                  label={'Cobrança do responsável por hora/aula '}
                  name="valueFromResponsible"
                  disabled
                  onChange={(e) =>
                    handleChangeMoneyMask(e, 'valueFromResponsible')
                  }
                />
              )}
              {reportInfo?.valueToTeacher !== undefined && (
                <Input
                  label="Valor a ser pago ao professor por hora/aula"
                  name="valueToTeacher"
                  disabled
                  onChange={(e) => handleChangeMoneyMask(e, 'valueToTeacher')}
                />
              )}
              {reportInfo?.valueToTrip !== undefined && (
                <Input
                  label="Valor do deslocamento por aula"
                  name="valueToTrip"
                  disabled
                  onChange={(e) => handleChangeMoneyMask(e, 'valueToTrip')}
                />
              )}
              <Switch
                label="O aluno faltou?"
                name="wasStudentAbsent"
                disabled={!canTeacherUpdates}
              />
              <TextArea
                label="Conteúdo Ministrado / Comentário sobre a aula"
                name="taughtContent"
                disabled={studentAbsent || !canTeacherUpdates}
                rules={
                  studentAbsent ? undefined : formRules.taughtContentRequired
                }
              />
              <div className="flex flex-col items-center justify-center">
                <label className="font-bold">
                  O desempenho do aluno melhorou em relação à última aula?
                </label>
                <Radio
                  name="studentPerformance"
                  disabled={studentAbsent || !canTeacherUpdates}
                  options={studentPerformance}
                  direction="row"
                />
              </div>
              {reportInfo?.generalObservation !== undefined && (
                <TextArea
                  label="Observações adicionais"
                  name="generalObservation"
                  disabled={!canTeacherUpdates}
                />
              )}

              <ModalReviewReq
                open={openReviewModal}
                onClose={() => setOpenReviewModal(false)}
                isAdminOrEmployee={isAdmOrEmployee}
                reportInfoId={reportInfo?.id}
                reportDefaultValues={reportDefaultValues}
                onUpdate={onUpdate}
                setDefaultStatus={() =>
                  methods.setValue('status', reportDefaultValues.status)
                }
              />

              {(isAdmOrEmployee || canTeacherUpdates) && !hideButtons && (
                <div className="flex justify-end gap-6 mt-3">
                  <Button
                    className="bg-orange-light"
                    onClick={onClose}
                    circular
                    fullWidth={false}
                  >
                    Cancelar
                  </Button>

                  <Button
                    circular
                    type="submit"
                    fullWidth={false}
                    isLoading={isUpdatingReport}
                  >
                    Salvar
                  </Button>
                </div>
              )}
            </form>
          </FormProvider>
        </div>
      </div>
    </DraggableModal>
  );
}
